import { defaultExpiryDateIso } from '../hostedReport.js';
import type { HostedReportState } from '../../types/hostedReport.js';
import type {
  PortalLibrarySection,
  PortalLibraryTileState,
} from '../../types/portalLibrary.js';

// CLIENT PORTAL LIBRARY — the clocks, the period and the tags. PURE, shared by
// the upload form and the API so the two cannot disagree.

// ---------- the portal clock (brief §3b) ----------

/**
 * Days until a tile comes down by default, per section. ⚠️ `null` = NEVER.
 * "How to record the perfect video" is evergreen; a global 90-day default
 * would be wrong for two of three sections, and a default that is usually
 * wrong trains people to click the tick without reading it.
 */
export const PORTAL_LIBRARY_DEFAULT_VISIBLE_DAYS: Record<PortalLibrarySection, number | null> = {
  college: null,
  'strategy.monthly': 90,
  'strategy.alternate': null,
  // Round 6: evergreen, like Alternate — a campaign deck is kept until it is taken
  // down, and a custom report is a one-off built to stay.
  'strategy.campaigns': null,
  'reports.custom': null,
};

/**
 * The form's default `visibleUntil` for a section, as the `yyyy-mm-dd` the
 * date picker emits — or null for "never". Local calendar, like every other
 * expiry date the staff app picks.
 */
export function defaultVisibleUntil(section: PortalLibrarySection, nowMs: number): string | null {
  const days = PORTAL_LIBRARY_DEFAULT_VISIBLE_DAYS[section];
  return days === null ? null : defaultExpiryDateIso(nowMs, days);
}

/**
 * Where a tile stands right now.
 *
 * ⚠️ ORDER IS THE DESIGN. A MISSING or REVOKED report is reported first: those
 * are staff actions on the file and need fixing on the file, whatever the
 * tile's own dates say. Then the tile's own clock (`ended`), which is the
 * intended way for a tile to come down. `reportExpired` with the tile still
 * inside its window is DRIFT — the two dates are meant to be equal — so it is
 * named rather than hidden under `ended`. Only then `scheduled` and `live`.
 *
 * ⚠️ Every null check is explicit: `null <= now` is TRUE in JavaScript.
 */
export function classifyTileState(
  tile: { visibleFromMs: number; visibleUntilMs: number | null },
  report: HostedReportState | 'missing',
  nowMs: number,
): PortalLibraryTileState {
  if (report === 'missing') return 'reportMissing';
  if (report === 'revoked') return 'reportRevoked';
  if (tile.visibleUntilMs !== null && tile.visibleUntilMs <= nowMs) return 'ended';
  if (report === 'expired') return 'reportExpired';
  if (tile.visibleFromMs > nowMs) return 'scheduled';
  return 'live';
}

// ---------- the period (brief §7) ----------

const PERIOD_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;

export function isValidPeriod(value: string): boolean {
  return PERIOD_RE.test(value);
}

/** ⚠️ Required for `strategy.monthly`, null everywhere else — both directions. */
export function periodError(section: PortalLibrarySection, period: string | null): string | null {
  if (section === 'strategy.monthly') {
    if (period === null || period.trim() === '') {
      return 'A monthly campaign needs its month (yyyy-mm).';
    }
    if (!isValidPeriod(period)) return 'The month must be written yyyy-mm, e.g. 2026-10.';
    return null;
  }
  if (period !== null) return 'Only a monthly campaign carries a month.';
  return null;
}

// ---------- category (Round 4 §6) ----------

/**
 * ⚠️ The category rule depends on the SECTION, exactly like `period`, so it is
 * a schema refinement ON THE SECTION rather than a form check. Exactly one for
 * `college`; none anywhere else (a taxonomy on a section that does not need
 * one is an empty chip row).
 */
export function categoryError(
  section: PortalLibrarySection,
  categoryId: string | null,
): string | null {
  if (section === 'college') {
    return categoryId === null || categoryId.trim() === ''
      ? 'A Kyck college item needs a category.'
      : null;
  }
  return categoryId !== null ? 'Only Kyck college items have a category.' : null;
}

export const PORTAL_LIBRARY_CATEGORY_NAME_MAX = 60;

// ---------- tags (Amendment 1 §C) ----------

export const PORTAL_LIBRARY_TAG_MAX_LENGTH = 40;
export const PORTAL_LIBRARY_TAG_MAX_COUNT = 10;

/**
 * trim → lowercase → collapse internal whitespace. That kills "video" /
 * "Video" / "Video " — three of the four drift cases. "videos" vs "video"
 * survives; autocomplete handles that socially with this few authors.
 */
export function normaliseTag(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Normalised, blanks dropped, de-duplicated, in first-seen order. */
export function normaliseTags(raw: readonly string[]): string[] {
  const out: string[] = [];
  for (const t of raw) {
    const n = normaliseTag(t);
    if (n && !out.includes(n)) out.push(n);
  }
  return out;
}

/** The distinct tags already in use in a section, most used first. */
export function tagSuggestions(
  entries: readonly { section: PortalLibrarySection; tags: readonly string[] }[],
  section: PortalLibrarySection,
): string[] {
  const counts = new Map<string, number>();
  for (const e of entries) {
    if (e.section !== section) continue;
    for (const t of e.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([t]) => t);
}
